import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Component, HostListener} from '@angular/core';
import {ComponentBase} from '@drl/core/component-base';
import {AuthTokenSkipHeader} from '@drl/core/interceptors/auth.interceptor';
import {UserSessionStoreService as StoreService} from '@drl/core/store/user-session-store.service';
import {NbIconLibraries} from '@nebular/theme';

import {FontPack} from '@drl/core/enums';
import {IpsTranslateService} from '@drl/core/localization/ips-translate.service';
import * as FullStory from '@fullstory/browser';
import {environment} from '../environments/environment';
import {PasswordExpiry} from './password-expiry-popup/services/password-expiry.service';
import {PasswordExpiryEmitter} from './password-expiry-popup/services/password-expiry-emitter.service';
import {Meta, Title} from '@angular/platform-browser';
import {ConfigurationsFacadeService} from './shared/facades';
import {TranslateService} from '@ngx-translate/core';
import {GetTenantProfileCommand} from './core/store/commands';
import {TenantDetails} from './shared/models';
import {ApiService} from './core/api/api.service';
import {TenantProfileAdapter} from './shared/adapters';
import * as moment from 'moment';
import {Router} from '@angular/router';
import {AuthService} from './core/auth/auth.service';
import {PrimengCalendarLocaleService} from './core/localization/primeng-calendar-locale.service';

const loadingTenantDetailsLabel = 'loadingTenantDetails';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: [],
})
export class AppComponent extends ComponentBase {
  private readonly authHeaders = new HttpHeaders().set(AuthTokenSkipHeader, '');
  isCCOpened: boolean = false;
  registeredSvgPacks: any = {};

  constructor(
    private readonly store: StoreService,
    private readonly iconLibraries: NbIconLibraries,
    public readonly ipsTranslate: IpsTranslateService,
    private readonly passwordExpiryEmitterService: PasswordExpiryEmitter,
    private readonly configFacade: ConfigurationsFacadeService,
    private readonly apiService: ApiService,
    private readonly tenProfileAdapter: TenantProfileAdapter,
    private readonly titleService: Title,
    // sonarignore:start
    private meta: Meta,
    private http: HttpClient,
    private readonly authService: AuthService,
    private readonly calendarService: PrimengCalendarLocaleService,
    protected route?: Router, // sonarignore:end
  ) {
    super(route);
    this.setTitleandImage();
    this.registerIcons();
    if (environment.orgId) {
      FullStory.init({
        orgId: environment.orgId,
        debug: environment.debug,
        namespace: environment.namespace,
        recordCrossDomainIFrames: true,
        recordOnlyThisIFrame: false,
        devMode: !environment.production,
      });
    }
    this.translate = this.ipsTranslate.translate;
  }

  tenantName: string;
  passwordExpiry: PasswordExpiry;
  showPopup = false;
  translate: TranslateService;
  loading: boolean;
  loadingTenantDetails;

  async ngOnInit() {
    this.createCspMetaTag();
    await this.checkWindowReopen();
    await this.loadTranslations();
    // await this.checkWindowReopen(); // removed duplicate call
    await this.loadTenantDetails();

    const locale = this.store.getLocale();
    moment.locale(locale);

    this._subscriptions.push(
      this.passwordExpiryEmitterService.passwordExpiryTypeEmitter.subscribe(
        data => {
          this.passwordExpiry = data;
          this.showPopup = true;
        },
      ),
    );

    this.checkIfCareCentralPage();
  }

  closePopup() {
    this.showPopup = false;
  }

  createCspMetaTag() {
    const csp = `
      default-src 'self' ${
        environment.cspTenantApiUrl
      } https://*.netcoresmartech.com https://*.hansel.io;
      script-src 'self' 'unsafe-inline' 'unsafe-eval' ${
        environment.cspTenantApiUrl
      } https://*.netcoresmartech.com https://*.hansel.io;
      style-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://*.netcoresmartech.com https://*.hansel.io;
      img-src 'self' data: https://*.amazonaws.com;
      media-src 'self' data: https://*.amazonaws.com;
      connect-src 'self' ${environment.cspTenantApiUrl.replace(
        'http://',
        'https://',
      )} https://*.amazonaws.com https://*.pndsn.com https://*.netcoresmartech.com wss://*.hansel.io https://*.hansel.io;
      font-src 'self' https://fonts.gstatic.com https://*.hansel.io https://*.netcoresmartech.com;
    `
      .replace(/\s{2,}/g, ' ')
      .trim();

    this.meta.addTag(
      {
        'http-equiv': 'Content-Security-Policy',
        content: csp,
      },
      true,
    );
  }

  setTitleandImage() {
    this.titleService.setTitle(environment.brand);
    const favicon = document.getElementById('appFavicon') as HTMLLinkElement;
    if (favicon) {
      favicon.href = 'assets/favicon.png';
    }
  }

  getAndSaveSubdomain() {
    // const getSubdomainRegex = /(?:http[s]*\:\/\/)*(.*?)\.(?=[^\/]*\..{2,5})/i;

    // // Use host only, not full URL
    // const fullURL: string = window.location.host;
    // const result: RegExpMatchArray = fullURL.match(getSubdomainRegex);

    // let valueToSave: string;

    // if (result !== null) {
    //   const subdomain = result[1];

    //   if (subdomain && subdomain !== 'www') {
    //     valueToSave = subdomain; // a real subdomain → use it
    //   } else {
    //     const parts = fullURL.split('.');
    //     valueToSave = parts[parts.length - 3]; // domain "commigocare"
    //   }
    // } else {
    //   const parts = fullURL.split('.');
    //   valueToSave = parts[parts.length - 3]; // domain
    // }

    this.store.setTenantKeyName(environment.tenantName);
  }

  async loadTranslations() {
    this.ipsTranslate.init(this.store.getLocale());
    this.loadingTenantDetails = await this.translate
      .get(loadingTenantDetailsLabel)
      .toPromise();
  }

  async loadTenantDetails() {
    this.getAndSaveSubdomain();
    this.loading = true;
    if (this.store.getTenantKeyName()) {
      this._subscriptions.push(
        this.configFacade
          .getTenantDetailsByTenantKey(this.store.getTenantKeyName())
          .subscribe((res: TenantDetails) => {
            this.store.setTenantName(res.name);
            this.store.setTenantId(res.id);
            environment.pubKey = res.pubKey;
            environment.subKey = res.subKey;
            this.getTenantProfile(res);
            this.loading = false;
          }),
      );
    }
  }

  getTenantProfile(tenantDetails: TenantDetails) {
    const command: GetTenantProfileCommand<TenantDetails> =
      new GetTenantProfileCommand(this.apiService, this.tenProfileAdapter);

    command.parameters = {
      headers: this.authHeaders,
      query: new HttpParams().set('tenantName', tenantDetails.name),
    };

    this._subscriptions.push(
      command.execute().subscribe(profile => {
        this.translate.setDefaultLang(profile.locale);
        this.store.setCountryName(profile.country || 'India');
        this.store.setWhiteLabelled(profile.is_white_labelled || false);
        this.store.setTenantLogo(profile.logo);
        this.store.setMonochromaticTenantLogo(profile.monochrome_logo);
        this.loading = false;
      }),
    );
  }

  registerIcons() {
    this.iconLibraries.registerFontPack(FontPack.FontAwesomeSolid, {
      packClass: 'fas',
      iconClassPrefix: 'fa',
    });
    this.iconLibraries.registerFontPack(FontPack.FontAwesomeRegular, {
      packClass: 'far',
      iconClassPrefix: 'fa',
    });

    setTimeout(async () => {
      try {
        const iconsMap = {
          'patient-directory': 'assets/icons/patient-directory.svg',
          'care-central': 'assets/icons/care-central.svg',
          'patient-education': 'assets/icons/patient-education.svg',
          'user-management-settings':
            'assets/icons/user-management-settings.svg',
        };

        const pdiconSvg = await this.http
          .get(iconsMap['patient-directory'], {responseType: 'text'})
          .toPromise();

        this.iconLibraries.registerSvgPack('custom', {
          'patient-directory': pdiconSvg,
          'care-central': await this.http
            .get(iconsMap['care-central'], {responseType: 'text'})
            .toPromise(),
          'patient-education': await this.http
            .get(iconsMap['patient-education'], {responseType: 'text'})
            .toPromise(),
          'user-management-settings': await this.http
            .get(iconsMap['user-management-settings'], {responseType: 'text'})
            .toPromise(),
        });

        console.log(`Icons loaded and registered successfully`);
      } catch (error) {
        console.error('Error loading icons:', error);
      }
    }, 0);
  }

  checkIfCareCentralPage() {
    this._subscriptions.push(
      this.isCareCentralPage$.subscribe(isCareCentralPage => {
        this.isCCOpened = isCareCentralPage;
      }),
    );
  }

  @HostListener('window:beforeunload', ['$event'])
  onWindowBeforeUnload(event: Event) {
    // Remove this tab marker
    sessionStorage.removeItem('tab_active');

    // If no other tabs are open, clear global session
    setTimeout(() => {
      if (!this.anyTabOpen()) {
        localStorage.removeItem('session_active');
      }
    }, 0);
  }

  anyTabOpen(): boolean {
    // Check if any tab is active
    return !!localStorage.getItem('session_active');
  }

  async checkWindowReopen() {
    const isTabNew = !sessionStorage.getItem('tab_active');
    const isNewSession = !localStorage.getItem('session_active');

    // Mark this tab as active
    sessionStorage.setItem('tab_active', 'true');

    if (isNewSession && this.store.getAccessToken()) {
      // Refresh token only when all tabs were previously closed
      this._subscriptions.push(
        this.authService.refreshToken().subscribe({
          next: () => console.log('Token refreshed on new session start'),
          error: () => {
            this.authService.logout();
            this.route.navigate(['/login']);
          },
        }),
      );
    }

    // Mark session as active globally
    localStorage.setItem('session_active', 'true');
  }

  onWindowOpened() {
    this._subscriptions.push(this.authService.refreshToken().subscribe());
  }
}
